import { useEffect, useState, useContext, useCallback, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { GetVideoPeak, UpdateVideoPeak } from "../../../../Api/Video";
import { IPlayerPeakInfo } from "../../../../Models/IPlayerPeakInfo";
import { UserAuthenticationContext } from "../../../../Context/UserAuthenticationContext";

interface UseVideoPeakResult {
    peak: IPlayerPeakInfo | null;
    loading: boolean;
    error: string;
    updatePeak: (currentPeak: number) => Promise<boolean>;
}

interface PeakTarget {
    userId: string;
    videoId: number | undefined;
    loaded: boolean;
}

interface PeakState {
    target: PeakTarget;
    peak: IPlayerPeakInfo | null;
    loading: boolean;
    error: string;
}

interface QueuedWrite {
    seconds: number;
    resolve: (saved: boolean) => void;
}

interface WriteQueue {
    running: boolean;
    pending: QueuedWrite | null;
}

// Keep writes ordered even if a player unmounts and another mounts for the same video.
// Pending positions coalesce to the most recent playback position, including seeks back.
const writeQueues = new Map<string, WriteQueue>();

function queuePeak(userId: string, videoId: number, seconds: number): Promise<boolean> {
    const key = JSON.stringify([userId, videoId]);
    let queue = writeQueues.get(key);
    if (!queue) {
        queue = { running: false, pending: null };
        writeQueues.set(key, queue);
    }
    const activeQueue = queue;
    return new Promise(resolve => {
        // A superseded position was not persisted; its caller must not mark it saved.
        activeQueue.pending?.resolve(false);
        activeQueue.pending = { seconds, resolve };
        if (activeQueue.running) return;
        activeQueue.running = true;
        const drain = async () => {
            while (activeQueue.pending) {
                const next = activeQueue.pending;
                activeQueue.pending = null;
                let saved = false;
                try {
                    saved = await UpdateVideoPeak(userId, videoId, next.seconds);
                } catch {
                    // Keep the queue usable if a transport implementation rejects.
                }
                next.resolve(saved);
            }
            writeQueues.delete(key);
        };
        void drain();
    });
}

function useFetchVideoPeakInfo(videoId: number | undefined): UseVideoPeakResult {
    const { userData, userDataFirstFetchComplete, authInitializing } = useContext(UserAuthenticationContext);
    const userId = userData?.id || "";
    // Login/signup assign the already-fetched account directly, without setting
    // the bootstrap-only first-fetch flag. A resolved account is ready too.
    const authReady = !authInitializing && (!!userId || userDataFirstFetchComplete);
    const validVideo = typeof videoId === "number" && Number.isSafeInteger(videoId) && videoId > 0;
    const [session] = useState(() => uuidv4());
    const target = useMemo<PeakTarget>(() => ({ userId, videoId, loaded: false }), [userId, videoId]);
    const authRef = useRef({ userId, ready: authReady });
    authRef.current = { userId, ready: authReady };
    const [state, setState] = useState<PeakState | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        target.loaded = false;
        setState({ target, peak: null, loading: validVideo, error: "" });
        if (!validVideo || !authReady) {
            return () => controller.abort();
        }
        const makePeak = (currentPeak: number): IPlayerPeakInfo => ({
            session,
            videoId: videoId!,
            currentPeak,
            userId,
            blocked: false,
            updateBlockedState: blocked => {
                setState(previous => previous?.target === target && previous.peak
                    ? { ...previous, peak: { ...previous.peak, blocked } }
                    : previous);
            },
        });
        if (!userId) {
            setState({ target, peak: makePeak(0), loading: false, error: "" });
            return () => controller.abort();
        }
        let retryTimer: ReturnType<typeof setTimeout> | undefined;
        let fetching = false;
        const fetchPeak = async () => {
            if (fetching || target.loaded || controller.signal.aborted) return;
            clearTimeout(retryTimer);
            fetching = true;
            try {
                const result = await GetVideoPeak(userId, videoId!, controller);
                if (controller.signal.aborted) return;
                target.loaded = true;
                setState({ target, peak: makePeak(result.peak), loading: false, error: "" });
            } catch {
                if (controller.signal.aborted) return;
                setState({ target, peak: null, loading: false, error: "Failed to fetch video peak data." });
                retryTimer = setTimeout(() => { void fetchPeak(); }, 15000);
            } finally {
                fetching = false;
            }
        };
        const retryOnline = () => { void fetchPeak(); };
        window.addEventListener("online", retryOnline);
        void fetchPeak();
        return () => {
            controller.abort();
            clearTimeout(retryTimer);
            window.removeEventListener("online", retryOnline);
        };
    }, [target, userId, videoId, validVideo, authReady, session]);

    const updatePeak = useCallback((currentSeconds: number): Promise<boolean> => {
        const auth = authRef.current;
        if (!auth.ready || !target.userId || auth.userId !== target.userId || !target.loaded ||
            !target.videoId || !Number.isFinite(currentSeconds) || currentSeconds < 0) {
            return Promise.resolve(false);
        }
        return queuePeak(target.userId, target.videoId, Math.floor(currentSeconds));
    }, [target]);

    // Hide the previous video's/account's resume point during the first render of a transition.
    const current = state?.target === target && authReady ? state : null;
    return {
        peak: current?.peak ?? null,
        loading: validVideo && (!authReady || !current || current.loading),
        error: current?.error ?? "",
        updatePeak,
    };
}

export default useFetchVideoPeakInfo;
